import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
  total?: number;
}

export interface AgentMessageResult {
  message: string;
  conversation_id: string;
  message_id: string;
  provider: string;
}

/** One row of the `workspace_recent_views` collection — a distinct workspace view in the "⋯" menu.
 *  Snake_case because it is the wire shape; `useRecentViews` maps it to the camelCase `RecentEntry`. */
export interface RecentViewRow {
  entry_key: string;
  card_type: string;
  kind: string;
  title: string;
  subtitle?: string;
  context_ref?: { type: string; id: string } | null;
  view_params: Record<string, unknown>;
  source: string;
  message_id?: string | null;
  access_count?: number;
  last_accessed_at?: string;
  last_accessed_at_ms?: number;
}

/** Body for recording a user-initiated view (a manual filter, a `+` create, or a menu replay). */
export interface RecentViewBody {
  card_type: string;
  kind?: string;
  title?: string;
  subtitle?: string;
  context_ref?: { type: string; id: string } | null;
  view_params?: Record<string, unknown>;
  source?: string;
  message_id?: string | null;
}

export interface AgentConversation {
  conversation_id: string;
  session_id?: string;
  agent_id?: string;
  assistant_id?: string;
  title: string;
  created_at?: string;
  updated_at?: string;
  last_activity_at?: string;
}

export interface AgentMessage {
  message_id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  /** Entry type: user_message | assistant_message | ux_snapshot | agent_action | system_event */
  type?: string;
  content: string;
  metadata?: Record<string, unknown>;
  files?: unknown[];
  created_at?: string;
}

/** A history-search hit (message or UX-snapshot) with its session + related view. */
export interface AgentHistoryHit {
  conversation_id: string;
  session_title: string;
  type?: string;
  role?: string;
  content: string;
  caption?: string;
  module?: string;
  filter?: Record<string, unknown>;
  created_at?: string;
}

export interface SendAgentMessageBody {
  assistant_id: string;
  message: string;
  conversation_id?: string;
  interaction_mode?: 'text' | 'push_to_talk' | 'interactive';
  meta?: Record<string, unknown>;
  files?: unknown[];
}

/** Per-user chat configuration resolved from the `chat_settings` config. */
export interface ChatConfig {
  multi_thread_allowed: boolean;
  /** When multi-threading is off, the single default session id to use (same on every device). */
  default_session_id: string | null;
  /** When true, agent list/panel reads ALSO render an inline action card in the chat thread
   *  (the workspace header + "⋯" caption display is always on regardless). */
  action_card_inline: boolean;
  /** When true, each assistant reply gets a "View agent steps" inspector showing that turn's
   *  full execution trace (LLM call, tool calls/outputs, actions, response). Debug aid. */
  debug_mode: boolean;
  /** When true, the mobile workspace shows a "Tap to open chat" link on the reply summary that
   *  opens the chat thread at that reply. Off by default. */
  tap_to_open_chat: boolean;
  /** When true, show a text input alongside the mic/speaker controls during an interactive (voice)
   *  session, letting the user type a message straight to the live realtime agent. Off by default. */
  interactive_text_input_enabled: boolean;
  /** When true, voice note dictation can be ended by SAYING the stop word (hands-free) instead of
   *  tapping Stop; the trailing stop word is stripped from the saved note. Off by default. */
  note_stop_word_enabled: boolean;
  /** The spoken phrase that ends voice note dictation when note_stop_word_enabled is on. */
  note_stop_word: string;
  /** When true, each assistant reply shows a "Was this helpful?" thumbs up/down feedback prompt.
   *  Feedback is stored and visible to platform admins in the Chat Monitor. Off by default. */
  feedback: boolean;
  /** How many entries the workspace "⋯" recent-views menu shows — always the N most recently
   *  opened/accessed views. Older ones stay recorded server-side; they just drop out of the menu
   *  once more recent views push past this count. Admin-configurable (default 10). */
  recent_views_limit: number;
  /** Chat attachment limits (from the agent_chat catalogue) used to validate before upload. */
  file_upload: FileUploadConfig;
}

export interface FileUploadConfig {
  enabled: boolean;
  /** Allowed extensions, lowercase, no dot (e.g. ["pdf","png"]). */
  allowed_types: string[];
  max_size_mb: number;
  max_files: number;
}

/** A file uploaded to the chat upload endpoint, ready to send with a message. */
export interface UploadedFile {
  name: string;
  type: string;
  url: string;
}

/** One step in a turn's execution trace (a `conversation_runs.steps[]` entry). */
export interface AgentRunStep {
  step_type:
    | 'user_input' | 'llm_call' | 'tool_call' | 'tool_output'
    | 'agent_response' | 'ux_snapshot' | 'agent_action';
  /** string for text steps; object for llm_call/tool_call/tool_output. */
  content: unknown;
  metadata?: Record<string, unknown>;
  timestamp?: string;
}

/** A full turn trace. `message_id` ties it to its live chat bubble; reloaded bubbles use run_id. */
export interface AgentRun {
  run_id: string;
  message_id?: string | null;
  session_id: string;
  agent_id?: string;
  status?: string;
  error?: string | null;
  model_used?: string | null;
  provider_used?: string | null;
  created_at?: string;
  steps: AgentRunStep[];
}

export interface ClientConfig {
  ai: {
    stt_provider: string;
    tts_provider: string;
    tts_voice: string;
    llm_model: string;
  };
  voice: {
    interaction_mode_default: string;
    allowed_interaction_modes: string[];
    voice_provider: string;
    default_voice: string;
    available_voices: { id: string; name: string; gender?: string; description?: string; sample_url?: string }[];
  };
  chat: {
    multi_thread_allowed: boolean;
  };
}

export const agentApi = createApi({
  reducerPath: 'agentApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Conversation', 'ConversationList', 'RecentViews'],
  endpoints: (builder) => ({
    sendAgentMessage: builder.mutation<AgentMessageResult, SendAgentMessageBody>({
      query: (body) => ({ url: '/api/v1/chat/messages', method: 'POST', body }),
      transformResponse: (res: ApiResponse<AgentMessageResult>) => res.data,
      invalidatesTags: ['ConversationList'],
    }),
    uploadChatFiles: builder.mutation<UploadedFile[], File[]>({
      query: (files) => {
        const form = new FormData();
        files.forEach((f) => form.append('files', f));
        return { url: '/api/v1/chat/upload', method: 'POST', body: form };
      },
      transformResponse: (res: ApiResponse<UploadedFile[]>) => res.data,
    }),
    getChatConfig: builder.query<ChatConfig, { agentId?: string } | void>({
      query: (arg) => {
        const agentId = arg && 'agentId' in arg ? arg.agentId : undefined;
        return `/api/v1/chat/chat-config${agentId ? `?agent_id=${encodeURIComponent(agentId)}` : ''}`;
      },
      transformResponse: (res: ApiResponse<ChatConfig>) => res.data,
    }),
    getClientConfig: builder.query<ClientConfig, void>({
      query: () => '/api/v1/configurations/client',
      transformResponse: (res: ApiResponse<ClientConfig>) => res.data,
    }),
    listAgentConversations: builder.query<AgentConversation[], void>({
      query: () => '/api/v1/chat/conversations',
      transformResponse: (res: ApiResponse<AgentConversation[]>) => res.data,
      providesTags: ['ConversationList'],
    }),
    createAgentConversation: builder.mutation<AgentConversation, { agent_id: string; title?: string }>({
      query: (body) => ({ url: '/api/v1/chat/conversations', method: 'POST', body }),
      transformResponse: (res: ApiResponse<AgentConversation>) => res.data,
      invalidatesTags: ['ConversationList'],
    }),
    renameAgentConversation: builder.mutation<unknown, { id: string; title: string }>({
      query: ({ id, title }) => ({ url: `/api/v1/chat/conversations/${id}`, method: 'PATCH', body: { title } }),
      invalidatesTags: ['ConversationList'],
    }),
    deleteAgentConversation: builder.mutation<unknown, string>({
      query: (id) => ({ url: `/api/v1/chat/conversations/${id}`, method: 'DELETE' }),
      invalidatesTags: ['ConversationList'],
    }),
    getAgentConversation: builder.query<
      AgentConversation & { messages: AgentMessage[]; has_more?: boolean; total_messages?: number },
      { id: string; limit?: number; offset?: number }
    >({
      query: ({ id, limit, offset }) => {
        const params = new URLSearchParams();
        if (limit != null) params.set('limit', String(limit));
        if (offset != null) params.set('offset', String(offset));
        const qs = params.toString();
        return `/api/v1/chat/conversations/${id}${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (
        res: ApiResponse<AgentConversation & { messages: AgentMessage[]; has_more?: boolean; total_messages?: number }>,
      ) => res.data,
      providesTags: (_r, _e, arg) => [{ type: 'Conversation', id: arg.id }],
    }),
    searchAgentHistory: builder.query<AgentHistoryHit[], string>({
      query: (q) => `/api/v1/chat/history/search?q=${encodeURIComponent(q)}`,
      transformResponse: (res: ApiResponse<AgentHistoryHit[]>) => res.data,
    }),
    getAgentRecent: builder.query<AgentMessage[], { types?: string } | void>({
      query: (arg) => {
        const types = arg && 'types' in arg ? arg.types : undefined;
        return `/api/v1/chat/history/recent${types ? `?types=${encodeURIComponent(types)}` : ''}`;
      },
      transformResponse: (res: ApiResponse<AgentMessage[]>) => res.data,
    }),
    // Persist the synthetic "note captured" assistant bubble that ChatPage renders when the note
    // taker's own Stop button ends capture (no stop_note_taker tool call → no agent turn recorded).
    // Without this the bubble is visible only in the current tab and disappears on reload.
    recordNoteCapturedMessage: builder.mutation<
      { run_id: string } | { skipped: true },
      { conversationId: string; text: string; noteId?: string }
    >({
      query: ({ conversationId, text, noteId }) => ({
        url: `/api/v1/chat/conversations/${conversationId}/note-captured`,
        method: 'POST',
        body: { text, note_id: noteId },
      }),
      transformResponse: (res: ApiResponse<{ run_id: string } | { skipped: true }>) => res.data,
    }),
    // Debug "View agent steps": full run trace(s) for a turn. `turnId` is the bubble's id with any
    // `:a`/`:u` suffix stripped (matches the run's message_id for live turns, run_id for reloaded).
    getConversationRuns: builder.query<AgentRun[], { conversationId: string; turnId?: string }>({
      query: ({ conversationId, turnId }) =>
        `/api/v1/chat/conversations/${conversationId}/runs${turnId ? `?turn_id=${encodeURIComponent(turnId)}` : ''}`,
      transformResponse: (res: ApiResponse<AgentRun[]>) => res.data,
    }),
    submitMessageFeedback: builder.mutation<
      void,
      { conversationId: string; messageId: string; helpful: boolean; comment?: string | null }
    >({
      query: ({ conversationId, messageId, helpful, comment }) => ({
        url: `/api/v1/chat/conversations/${conversationId}/feedback`,
        method: 'POST',
        body: { message_id: messageId, helpful, comment: comment ?? null },
      }),
    }),
    // ── workspace recent views (the "⋯" menu) ───────────────────────────────────
    // Deduped + paginated server-side, so unlike the old message-derived history this pages on its
    // own instead of riding the chat thread's scroll-up pagination.
    listRecentViews: builder.query<
      { rows: RecentViewRow[]; total: number },
      { conversationId: string; skip?: number; limit?: number }
    >({
      query: ({ conversationId, skip = 0, limit = 20 }) =>
        `/api/v1/chat/conversations/${conversationId}/recent-views?skip=${skip}&limit=${limit}`,
      transformResponse: (res: ApiResponse<RecentViewRow[]>) => ({ rows: res.data, total: res.total ?? res.data.length }),
      providesTags: (_r, _e, arg) => [{ type: 'RecentViews', id: arg.conversationId }],
    }),
    // Only for views the USER opened directly (filter/facet/tab click, `+` create, replay) —
    // agent-driven ones are already recorded by publish_action on the server.
    recordRecentView: builder.mutation<RecentViewRow, { conversationId: string } & RecentViewBody>({
      query: ({ conversationId, ...body }) => ({
        url: `/api/v1/chat/conversations/${conversationId}/recent-views`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: ApiResponse<RecentViewRow>) => res.data,
    }),
    clearRecentViews: builder.mutation<{ deleted: number }, string>({
      query: (conversationId) => ({
        url: `/api/v1/chat/conversations/${conversationId}/recent-views`,
        method: 'DELETE',
      }),
      transformResponse: (res: ApiResponse<{ deleted: number }>) => res.data,
      invalidatesTags: (_r, _e, conversationId) => [{ type: 'RecentViews', id: conversationId }],
    }),
  }),
});

export const {
  useSendAgentMessageMutation,
  useUploadChatFilesMutation,
  useGetChatConfigQuery,
  useGetClientConfigQuery,
  useListAgentConversationsQuery,
  useGetAgentConversationQuery,
  useLazyGetAgentConversationQuery,
  useCreateAgentConversationMutation,
  useRenameAgentConversationMutation,
  useDeleteAgentConversationMutation,
  useSearchAgentHistoryQuery,
  useLazySearchAgentHistoryQuery,
  useGetAgentRecentQuery,
  useGetConversationRunsQuery,
  useRecordNoteCapturedMessageMutation,
  useSubmitMessageFeedbackMutation,
  useLazyListRecentViewsQuery,
  useRecordRecentViewMutation,
  useClearRecentViewsMutation,
} = agentApi;
